import React from "react";
import "./Popup.css";
import AspectRatio from "@mui/joy/AspectRatio";
import Card from "@mui/joy/Card";
import CardContent from "@mui/joy/CardContent";
import CardOverflow from "@mui/joy/CardOverflow";
import Divider from "@mui/joy/Divider";
import Typography from "@mui/joy/Typography";
import Stack from "@mui/joy/Stack";
import { publicationTime } from "./publication-time";


const regionNames = typeof Intl !== "undefined" && Intl.DisplayNames ? new Intl.DisplayNames(["en"], { type: "region" }) : null;
const countryName = (code) => {
  try { return (code && regionNames?.of(code)) || code || "Other"; } catch (_) { return code || "Other"; }
};

// Consecutive articles of the same event (storylines.js lists a point's
// articles together). An event covered by outlets in 2+ countries gets a
// "How the world sees it" comparison after its lead article.
function groupByEvent(articles) {
  const groups = [];
  articles.forEach((item, index) => {
    const eventId = item.story?.eventId;
    const last = groups[groups.length - 1];
    if (eventId && last?.eventId === eventId) last.items.push(item);
    else groups.push({ key: eventId || item.article_id, eventId, story: item.story, items: [item], first: index });
  });
  for (const g of groups) {
    const full = g.items.filter((a) => a.title != null);
    g.countries = [...new Set(full.map((a) => a.outletCountry).filter(Boolean))];
    g.compare = Boolean(g.story) && full.length >= 2 && g.countries.length >= 2 && g.items[0].title != null;
  }
  return groups;
}

const cardProps = { color: "primary", invertedColors: true, orientation: "vertical", size: "md", variant: "solid", sx: { width: 320 } };

function LoadingCard({ item }) {
  // Past days draw first from compact timeline data (position, country,
  // time); the title, link and image follow with the full articles.
  return (
    <Card {...cardProps} aria-busy="true">
      <CardContent>
        <Typography level="title-md">Loading story…</Typography>
        <Typography level="body-xs" fontWeight="md" textColor="text.secondary">
          {[item.country, publicationTime(item.publishedAt)].filter(Boolean).join(" · ")}
        </Typography>
      </CardContent>
    </Card>
  );
}

function ArticleCard({ item, index, isMobile, onOutbound, shortenString }) {
  return (
    <a href={item.link} key={item.article_id} target="_blank" rel="noreferrer" onClick={onOutbound}>
      <Card color="primary" invertedColors orientation="vertical" size="md" variant="solid" sx={{ width: 320 }}>
        {(!isMobile || index > 0) && item.image != null && (
          <CardOverflow>
            <AspectRatio ratio="2">
              <img src={item.image} srcSet={item.image} loading="lazy" alt="" />
            </AspectRatio>
          </CardOverflow>
        )}
        <CardContent>
          <Typography level="title-md">{item.title}</Typography>
          <Typography variant="plain" color="netural" level="body-sm">
            {item.description && shortenString(item.description)}
          </Typography>
          <Typography level="body-xs" fontWeight="md" textColor="text.secondary">{item.source}</Typography>
          {item.translatedFrom && (
            <Typography level="body-xs" textColor="text.secondary" title={item.originalTitle}>
              Machine-translated from {item.translatedFrom}
            </Typography>
          )}
        </CardContent>
        <CardOverflow variant="soft" sx={{ bgcolor: "background.level1" }}>
          <Divider inset="context" />
          <CardContent orientation="horizontal">
            <Typography level="body-xs" fontWeight="md" textColor="text.secondary">Mapped near {item.location}</Typography>
            <Divider orientation="vertical" />
            <Typography level="body-xs" fontWeight="md" textColor="text.secondary">{publicationTime(item.date)}</Typography>
          </CardContent>
          <Typography level="body-xs" sx={{ px: 2, pb: 1 }}>
            {item.locationPrecision === "country" ? "Country-level location" : "Approximate location"}
          </Typography>
        </CardOverflow>
      </Card>
    </a>  );
}

// A special story (artemis-mission.js): the card replays its event rather
// than linking out, so it isn't wrapped in the article link.
function MissionCard({ item, onOutbound, onWatchMission }) {
  return (
    <Card color="primary" invertedColors orientation="vertical" size="md" variant="solid" sx={{ width: 320 }}>
      <CardContent>
        <Typography level="body-xs" fontWeight="lg" textColor="text.secondary">Mission replay</Typography>
        <Typography level="title-md">{item.title}</Typography>
        <Typography variant="plain" level="body-sm">{item.description}</Typography>
        <button type="button" className="mission-watch" onClick={() => onWatchMission?.(item.special)}>
          Watch the mission
        </button>
        <Typography level="body-xs">
          <a href={item.link} target="_blank" rel="noreferrer" onClick={onOutbound}>NASA mission page</a>
        </Typography>
      </CardContent>
      <CardOverflow variant="soft" sx={{ bgcolor: "background.level1" }}>
        <Divider inset="context" />
        <CardContent orientation="horizontal">
          <Typography level="body-xs" fontWeight="md" textColor="text.secondary">{item.location}</Typography>
          <Divider orientation="vertical" />
          <Typography level="body-xs" fontWeight="md" textColor="text.secondary">{publicationTime(item.date)}</Typography>
        </CardContent>
      </CardOverflow>
    </Card>
  );
}

function ComparisonCard({ group, onOutbound }) {
  // One headline per outlet country first, then the rest, so the list shows
  // the spread of perspectives before repeats.
  const seen = new Set([group.items[0].outletCountry]);
  const firsts = [];
  const rest = [];
  for (const a of group.items.slice(1)) {
    if (a.title == null) continue;
    if (seen.has(a.outletCountry)) rest.push(a);
    else { seen.add(a.outletCountry); firsts.push(a); }
  }
  const rows = [...firsts, ...rest];
  return (
    <Card {...cardProps} aria-label="How the world sees it">
      <CardContent>
        <Typography level="title-sm">How the world sees it</Typography>
        <Typography level="body-xs" textColor="text.secondary">
          Covered by {group.story.sources} outlets in {group.countries.length} countries
        </Typography>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {rows.slice(0, 8).map((a) => (
            <a key={a.article_id} href={a.link} target="_blank" rel="noreferrer" onClick={onOutbound} className="compare-row">
              <Typography level="body-xs" fontWeight="lg" textColor="text.secondary">{countryName(a.outletCountry)} · {a.source}</Typography>
              <Typography level="body-sm">{a.title}</Typography>
              {a.translatedFrom && <Typography level="body-xs" textColor="text.secondary">Translated from {a.translatedFrom}</Typography>}
            </a>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

function EarlierCard({ earlier }) {
  return (
    <Card {...cardProps} variant="soft" aria-label="Earlier in this story">
      <CardContent>
        <Typography level="title-sm">Earlier in this story</Typography>
        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
          {earlier.slice(-5).map((e) => (
            <Typography key={e.eventId} level="body-xs">
              <strong>{publicationTime(e.start).replace(/ UTC$/, "")}</strong> · {e.title || "Earlier coverage"}
            </Typography>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

const Popup = ({articles, isMobile, isStart, onOutbound, onWatchMission}) => {
  const shortenString = (str) =>
    str.split(" ").slice(0, 20).join(" ") +
    (str.split(" ").length > 20 ? "..." : "");

  return (
    isStart ?
    <div className="popup info">
    <Card
              color="primary"
              invertedColors
              orientation="vertical"
              size="md"
              variant="solid"
              sx={{ width: 320 }}
            >
    <Typography level="title-md">
            Welcome to paleblue.news
          </Typography>
          <Typography variant="plain" color="netural" level="body-sm">
            World news from a global perspective.
          </Typography>
          <Typography variant="plain" color="netural" level="body-sm">
            Scroll around and explore today's news from all over the globe.
          </Typography>
          <Typography variant="plain" color="netural" level="body-sm">
            We believe that this view provides important global context that is lost when browsing traditional news websites.
          </Typography>
          <Typography variant="plain" color="netural" level="body-sm">
            Try the time travel feature to see the news for a date in the past.
          </Typography>
          <Typography variant="plain" color="netural" level="body-sm">
            This concept is inspired by the <a href="https://en.wikipedia.org/wiki/Overview_effect">overview effect</a>.
            The name is a reference to <a href="https://en.wikipedia.org/wiki/Pale_Blue_Dot">Pale Blue Dot</a>
          </Typography>
          <Typography variant="plain" color="netural" level="body-sm">
            <a href="mailto:contact@paleblue.news">
              Email us
            </a>
          </Typography>
          </Card></div> :
    <div className="popup articles">
      <Stack spacing={2}>
        {groupByEvent(articles).map((group) => (
          <React.Fragment key={group.key}>
            {group.items.map((item, i) => (group.compare && i > 0 ? null :
              item.title == null ? <LoadingCard key={item.article_id} item={item} /> :
              item.special ? <MissionCard key={item.article_id} item={item} onOutbound={onOutbound} onWatchMission={onWatchMission} /> :
              <ArticleCard key={item.article_id} item={item} index={group.first + i} isMobile={isMobile} onOutbound={onOutbound} shortenString={shortenString} />
            ))}
            {group.compare && <ComparisonCard group={group} onOutbound={onOutbound} />}
            {group.story?.earlier?.length > 0 && <EarlierCard earlier={group.story.earlier} />}
          </React.Fragment>
        ))}
      </Stack>
    </div>
  );
};
export default Popup;
